// src/App.js
import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";

import Header from "./components/Header";
import Footer from "./components/Footer";
import StudentSidebar from "./components/StudentSidebar";
import TeacherSidebar from "./components/TeacherSidebar";
import SuperAdminSidebar from "./components/SuperAdminSidebar";
import SchoolHomeSlider from "./components/SchoolHomeSlider";

import Home from "./pages/Home";
import Services from "./pages/Services";
import ServiceDetail from "./pages/ServiceDetail";
import Profile from "./pages/Profile";
import ResetPassword from "./pages/ResetPassword";
import ForgotPassword from "./pages/ForgotPassword";
import SetPassword from "./pages/SetPassword";
import AboutUs from "./components/AboutUs";
import FeedbackForm from "./pages/FeedbackForm";
import LoggedOut from "./pages/LoggedOut";

import AdmissionForm from "./components/AdmissionForm";
import ViewAdmissionForm from "./components/ViewAdmissionForm";
import AdmissionDetails from "./pages/AdmissionDetails";
import AdminAdmissions from "./components/AdminAdmissions";
import AdminClassSubjectManagement from "./components/AdminClassSubjectManagement";
import AdminExamManagement from "./components/AdminExamManagement";
import AdminFeeManagement from "./components/AdminFeeManagement";
import AdminNotificationsManagement from "./components/AdminNotificationsManagement";
import AdminParentPortal from "./components/AdminParentPortal";
import AdminPermissionMatrix from "./components/AdminPermissionMatrix";
import AdminStudentManagement from "./components/AdminStudentManagement";
import AdminStudentCreate from "./components/AdminStudentCreate";
import AdminStudentEdit from "./components/AdminStudentEdit";
import AdminStudentCustomizationRequest from "./components/AdminStudentCustomizationRequest";
import AdminCustomizationRequest from "./components/AdminCustomizationRequest";
import AdminStudentImport from "./components/AdminStudentImport";
import AdminModuleImport from "./components/AdminModuleImport";
import AdminTeacherManagement from "./components/AdminTeacherManagement";
import AdminTeacherCreate from "./components/AdminTeacherCreate";
import AdminTimetableManagement from "./components/AdminTimetableManagement";
import AdminUserManagement from "./components/AdminUserManagement";
import AdminLearningResourceRequests from "./components/AdminLearningResourceRequests";
import StudentLearningLibrary from "./components/StudentLearningLibrary";

import Login from "./components/Login";
import Gallery from "./components/Galary";
import MomentsPage from "./components/MomentsPage";
import Unauthorized from "./components/Unauthorized";

import AdminDashBoard from "./components/AdminDashBoard";
import AdminHome from "./components/AdminHome";
import AdminSidebar from "./components/AdminSidebar";
import UserDashBoard from "./components/UserDashBoard";
import TeacherDashboard from "./components/TeacherDashboard";
import ClassroomMeetingPage from "./components/ClassroomMeetingPage";
import SchoolDashboard from "./components/SchoolDashboard";
import PrincipalDashboard from "./components/PrincipalDashboard";
import SuperAdminSaaS from "./components/SuperAdminSaaS";
import StudentTimeSpent from "./components/StudentTimeSpent";
import TeacherStudentUsageView from "./components/TeacherStudentUsageView";
import SuperAdminPlatformMetrics from "./components/admin/SuperAdminPlatformMetrics";
import SuperAdminCustomizationRequests from "./components/admin/SuperAdminCustomizationRequests";
import SuperAdminBrandingApprovals from "./components/admin/SuperAdminBrandingApprovals";
import SuperAdminSchoolLeads from "./components/admin/SuperAdminSchoolLeads";
import SuperAdminEmployeeManagement from "./components/admin/SuperAdminEmployeeManagement";
import SuperAdminEmployeeCreate from "./components/admin/SuperAdminEmployeeCreate";
import SuperAdminHome from "./components/admin/SuperAdminHome";
import TeacherHome from "./components/TeacherHome";
import SuperAdminTenantForm from "./components/admin/SuperAdminTenantForm";
import SuperAdminDevActivityPage from "./components/admin/SuperAdminDevActivityPage";
import SuperAdminLoginAds from "./components/admin/SuperAdminLoginAds";
import SuperAdminLearningResources from "./components/admin/SuperAdminLearningResources";
import SchoolDemoRequest from "./components/SchoolDemoRequest";
import ClassNXLoader from "./components/ClassNXLoader";
import { useAppTheme } from "./context/AppThemeContext";
import { StudentProfileProvider } from "./context/StudentProfileContext";
import { TeacherProfileProvider } from "./context/TeacherProfileContext";
import StudentService from "./service/StudentService";
import TeacherService from "./service/TeacherService";

import { setupTokenExpiryWarning } from "./utils/tokenExpiryHandler";
import { requestSessionRefresh } from "./api/httpClient";
import SubscriptionExpired from "./pages/SubscriptionExpired";
import SubscriptionPayment from "./pages/SubscriptionPayment";
import SubscriptionPlans from "./pages/SubscriptionPlans";
import PlanUsage from "./pages/PlanUsage";
import Guide from "./pages/Guide";
import FirstLoginTour from "./components/FirstLoginTour";
import TenantBranding from "./pages/TenantBranding";
import SchoolHomeSliderManager from "./pages/SchoolHomeSliderManager";
import PublicTenantHome from "./pages/PublicTenantHome";
import { useTenant, hasSubscriptionAccess } from "./context/TenantContext";
import api, { initialiseCsrf } from "./api/httpClient";
import { API_PATHS } from "./api/apiPaths";
import {
  ADMIN_ROLES,
  ALL_APP_ROLES,
  MANAGEMENT_ROLES,
  normalizeRole,
  PARENT_ROLES,
  ROLE_ADMIN,
  ROLE_PRINCIPAL,
  ROLE_SCHOOL,
  ROLE_STAFF,
  ROLE_SUPER_ADMIN,
  ROLE_TEACHER,
  SCHOOL_ROLES,
  STAFF_ROLES,
  STUDENT_ROLES,
  getDashboardPathForRole,
} from "./utils/authRole";
import { canAccessModule, extractPermissionRules } from "./utils/permissionState";
import { clearSessionData, getStoredAuthSnapshot, terminateSession } from "./utils/sessionManager";
import { getCachedOrFetch, getTenantCacheKey } from "./utils/requestCache";
import { buildTenantSubdomainUrl } from "./utils/tenantState";

const SessionModal = ({
  mode,
  message,
  isBusy,
  onClose,
  onContinue,
}) => {
  if (!mode) return null;

  const isExpired = mode === "expired";

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/60 px-4 backdrop-blur-sm">
      <div className="w-full max-w-md overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/20">
        <div className={`h-2 ${isExpired ? "bg-rose-500" : "bg-amber-500"}`} />
        <div className="p-6 sm:p-7">
          <div className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl ${isExpired ? "bg-rose-50 text-rose-600" : "bg-amber-50 text-amber-600"}`}>
            {isExpired ? "!" : "i"}
          </div>
          <h2 className="mt-4 text-2xl font-extrabold text-slate-900">
            {isExpired ? "Session expired" : "Session ending soon"}
          </h2>
          <p className="mt-3 text-sm leading-6 text-slate-600">
            {message}
          </p>

          <div className="mt-6 flex flex-wrap gap-3">
            {!isExpired && (
              <button
                type="button"
                onClick={onContinue}
                disabled={isBusy}
                className="inline-flex items-center rounded-full bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {isBusy ? "Refreshing..." : "Stay signed in"}
              </button>
            )}
            <Link
              to="/login"
              onClick={onClose}
              className={`inline-flex items-center rounded-full px-5 py-2.5 text-sm font-semibold transition ${isExpired ? "bg-rose-600 text-white hover:bg-rose-700" : "border border-slate-300 text-slate-700 hover:bg-slate-50"}`}
            >
              Go to login
            </Link>
            {!isExpired && (
              <button
                type="button"
                onClick={onClose}
                disabled={isBusy}
                className="inline-flex items-center rounded-full border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-70"
              >
                Dismiss
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// const AdminBoardBackButton = ({ role }) => {
//   const location = useLocation();
//   const navigate = useNavigate();
//   const normalizedRole = normalizeRole(role);
//   const isManagementUser = MANAGEMENT_ROLES.includes(normalizedRole) && normalizedRole !== ROLE_SUPER_ADMIN;

//   if (!isManagementUser) return null;

//   const isAdminSubPage =
//     location.pathname.startsWith("/admin/")
//     || location.pathname === "/admission-details"
//     || location.pathname === "/gallery";

//   const hideOn = location.pathname === "/admin-board" || location.pathname === "/super-admin";

//   if (!isAdminSubPage || hideOn) return null;

//   const handleBack = () => {
//     if (window.history.length > 1) {
//       navigate(-1);
//       return;
//     }
//     navigate("/admin-board");
//   };

//   return (
//     <div className="mx-auto w-full max-w-7xl px-3 pb-2 pt-3 md:px-6">
//       <button
//         type="button"
//         onClick={handleBack}
//         className="theme-secondary-action inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition"
//       >
//         ← Back to Admin Board
//       </button>
//     </div>
//   );
// };

const isPublicRoute = (pathname = "") => (
  pathname === "/"
  || pathname === "/services"
  || pathname.startsWith("/services/")
  || pathname === "/about"
  || pathname === "/schools/request-demo"
  || pathname === "/feedback"
  || pathname === "/login"
  || pathname === "/forgot-password"
  || pathname === "/logged-out"
  || pathname === "/reset-password"
  || pathname === "/set-password"
  || pathname === "/unauthorized"
);

const AuthLoadingScreen = () => (
  <div className="app-shell">
    <ClassNXLoader variant="page" size="lg" label="Verifying your session" detail="Securing your ClassNX workspace." />
  </div>
);

// Password recovery and employee activation links can be opened in a browser
// that already has a ClassNX session. They must never inherit that user's UI.
const isStandaloneAuthRoute = (pathname = "") => (
  pathname === "/set-password"
);

const LandingRoute = ({ isLoggedIn, userRole, setUserRole }) => {
  const location = useLocation();
  const authError = new URLSearchParams(location.search).get("auth_error");
  const { isSubdomainTenant } = useTenant();

  if (authError) {
    return <Navigate to={`/login?auth_error=${encodeURIComponent(authError)}`} replace />;
  }

  if (isLoggedIn && userRole) {
    return <Navigate to={getDashboardPathForRole(userRole)} replace />;
  }

  // School tenant subdomains (e.g. fatowef.classnx.com) land on the school welcome homepage
  if (isSubdomainTenant) {
    return <PublicTenantHome userRole={userRole} />;
  }

  return <Home userRole={userRole} />;
};

const AppFrame = ({
  children,
  authReady,
  isDarkMode,
  isLoggedIn,
  userRole,
  setUserRole,
  isStudentUser,
  isTeacherUser,
  isSuperAdminUser,
  sessionModal,
  isRefreshingSession,
  closeSessionModal,
  handleContinueSession,
  permissionRules,
  permissionReady,
}) => {
  const location = useLocation();
  const { activeTenant } = useTenant();
  const [isAdminSidebarCollapsed, setIsAdminSidebarCollapsed] = useState(() => {
    try { return window.localStorage.getItem("adminSidebar:collapsed") === "true"; }
    catch { return false; }
  });
  const toggleAdminSidebar = () => {
    setIsAdminSidebarCollapsed((current) => {
      const next = !current;
      try { window.localStorage.setItem("adminSidebar:collapsed", String(next)); } catch { /* Keep in-memory preference. */ }
      return next;
    });
  };
  const standaloneAuthRoute = isStandaloneAuthRoute(location.pathname);
  const publicPasswordRoute = location.pathname === "/forgot-password" || location.pathname === "/reset-password";
  const showShell = authReady
    && !standaloneAuthRoute
    && (isLoggedIn || isPublicRoute(location.pathname));
  const normalizedRole = normalizeRole(userRole);
  const isSubscriptionAdmin = [ROLE_ADMIN, ROLE_SCHOOL].includes(normalizedRole);
  const isReadOnly = isLoggedIn
    && isSubscriptionAdmin
    && Boolean(activeTenant?.subscription)
    && !hasSubscriptionAccess(activeTenant.subscription);
  const showSchoolHomeSlider = isLoggedIn
    && normalizedRole !== ROLE_SUPER_ADMIN
    && ["/admin-home", "/teacher/home", "/user-dashboard", "/principal", "/school-dashboard"].includes(location.pathname);
  const isAdminWorkspaceRoute = location.pathname === "/admin-home"
    || location.pathname === "/admin-board"
    || location.pathname === "/settings/branding"
    || location.pathname === "/profile"
    || location.pathname === "/moments"
    || location.pathname.startsWith("/admin/");
  const showAdminSidebar = showShell
    && isLoggedIn
    && !isSuperAdminUser
    && MANAGEMENT_ROLES.includes(normalizedRole)
    && normalizedRole !== ROLE_PRINCIPAL
    && isAdminWorkspaceRoute
    ;

  if (!authReady) return <AuthLoadingScreen />;

  return (
    <div className={`app-shell h-screen min-h-screen flex flex-col overflow-x-hidden ${isDarkMode ? "app-theme-dark" : ""}`}>
      {showShell && <Header userRole={publicPasswordRoute ? null : userRole} setUserRole={setUserRole} hasAdminSidebar={showAdminSidebar && !publicPasswordRoute} />}

      <main className={`flex min-h-0 flex-1 overflow-hidden transition-[padding] duration-300 ${showShell ? "pb-[4.25rem] pt-[4.5rem]" : ""} ${showShell && !publicPasswordRoute && (isSuperAdminUser || isStudentUser || isTeacherUser || showAdminSidebar) ? (showAdminSidebar && isAdminSidebarCollapsed ? "lg:pl-[76px]" : "lg:pl-[248px]") : ""}`}>
        <div className="flex min-h-0 min-w-0 flex-1 overflow-hidden">
          {showShell && !publicPasswordRoute && isStudentUser && <StudentSidebar />}
          {showShell && !publicPasswordRoute && isTeacherUser && <TeacherSidebar />}
          {showShell && !publicPasswordRoute && isSuperAdminUser && <SuperAdminSidebar />}
          <div className="min-h-0 min-w-0 flex-1 overflow-y-auto">
            {/* {showShell && <AdminBoardBackButton role={userRole} />} */}
            {showAdminSidebar ? (
              <div className="flex min-h-full flex-col px-3 pb-6 pt-2 md:px-6 md:pb-8 md:pt-3">
                {isReadOnly && <ReadOnlySubscriptionNotice />}
                <AdminSidebar
                  role={normalizedRole}
                  permissionRules={permissionRules}
                  permissionReady={permissionReady}
                  isCollapsed={isAdminSidebarCollapsed}
                  onToggleCollapsed={toggleAdminSidebar}
                />
                <div className="min-h-0 min-w-0 flex-1">{showSchoolHomeSlider && <SchoolHomeSlider />}{children}</div>
              </div>
            ) : <>{isReadOnly && <ReadOnlySubscriptionNotice />}{showSchoolHomeSlider && <SchoolHomeSlider />}{children}</>}
          </div>
        </div>
      </main>

      {showShell && <Footer isLoggedIn={isLoggedIn} hasAdminSidebar={showAdminSidebar} />}
      {showShell && isLoggedIn && sessionModal.mode === "warning" && (
        <SessionModal
          mode={sessionModal.mode}
          message={sessionModal.message}
          isBusy={isRefreshingSession}
          onClose={closeSessionModal}
          onContinue={handleContinueSession}
        />
      )}
      <ToastContainer
        position="top-center"
        autoClose={3500}
        newestOnTop
        pauseOnHover
      />
    </div>
  );
};

const ReadOnlySubscriptionNotice = () => (
  <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950" role="status">
    <span><strong>Read-only mode.</strong> Your school subscription has expired; changes are disabled until renewal.</span>
    <Link to="/subscription-expired" className="inline-flex items-center gap-2 font-bold underline underline-offset-2">Renew subscription</Link>
  </div>
);

const App = () => {
  const { isDarkMode } = useAppTheme();
  const [userRole, setUserRole] = useState(null);
  const [onboardingUserIdentity, setOnboardingUserIdentity] = useState("");
  const userRoleRef = useRef(userRole);
  useEffect(() => { userRoleRef.current = userRole; }, [userRole]);
  const normalizedUserRole = normalizeRole(userRole);
  const isStudentUser = STUDENT_ROLES.includes(normalizedUserRole);
  const isTeacherUser = normalizedUserRole === ROLE_TEACHER;
  const isSuperAdminUser = normalizedUserRole === ROLE_SUPER_ADMIN;
  const adminBoardRoles = MANAGEMENT_ROLES.filter((role) => role !== ROLE_SUPER_ADMIN && role !== ROLE_PRINCIPAL);
  const [authReady, setAuthReady] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [sessionVersion, setSessionVersion] = useState(0);
  const [permissionRules, setPermissionRules] = useState([]);
  const [permissionReady, setPermissionReady] = useState(false);
  const [isRefreshingSession, setIsRefreshingSession] = useState(false);
  const [sessionModal, setSessionModal] = useState({
    mode: null,
    message: "",
  });
  useEffect(() => {
    const cleanup = setupTokenExpiryWarning();
    return cleanup;
  }, [userRole, sessionVersion]);

  useEffect(() => {
    let mounted = true;
    let sessionEstablishedDuringBootstrap = false;
    const markSessionEstablished = (event) => {
      sessionEstablishedDuringBootstrap = true;
      const session = event?.detail?.session || {};
      const sessionUser = session.user || session.account || {};
      const identity = session.userId || session.id || session.email || session.username || session.sub
        || session.principalId || sessionUser.userId || sessionUser.id || sessionUser.email || sessionUser.username;
      setOnboardingUserIdentity(identity ? String(identity) : "");
    };
    window.addEventListener("auth:session-established", markSessionEstablished);

    api.get(API_PATHS.auth.session, { withCredentials: true })
      .then(async (response) => {
        if (!mounted) return;
        const body = response?.data?.data ?? response?.data ?? {};
        const role = normalizeRole(body?.role);
        if (!role) throw new Error("Not authenticated");

        await initialiseCsrf();
        if (!mounted) return;
        window.localStorage.setItem("user", role);
        if (body?.name) window.localStorage.setItem("currentUserName", body.name);
        setUserRole(role);
        setSessionExpired(false);
        window.dispatchEvent(new CustomEvent("auth:session-established", {
          detail: { session: body },
        }));
      })
      .catch((error) => {
        if (!mounted) return;
        // Do not let an in-flight startup check erase a session that was
        // established by a successful login while that check was pending.
        if (sessionEstablishedDuringBootstrap) return;
        const status = Number(error?.status || error?.response?.status || 0);
        // A backend outage or internal error is not an authentication result.
        // Only an explicit unauthenticated/forbidden response clears UI state.
        if (status && status !== 401 && status !== 403) return;
        // Cached UI metadata is never proof of a valid backend session.
        clearSessionData();
        setUserRole(null);
      })
      .finally(() => {
        if (mounted) setAuthReady(true);
      });

    return () => {
      mounted = false;
      window.removeEventListener("auth:session-established", markSessionEstablished);
    };
  }, []);

  useEffect(() => {
    if (!authReady) return undefined;

    const requiresPermissionLookup = MANAGEMENT_ROLES.includes(normalizedUserRole)
      && normalizedUserRole !== ROLE_SUPER_ADMIN
      && normalizedUserRole !== ROLE_PRINCIPAL;

    if (!requiresPermissionLookup) {
      setPermissionRules([]);
      setPermissionReady(true);
      return undefined;
    }

    let mounted = true;
    setPermissionReady(false);

    getCachedOrFetch(
      getTenantCacheKey("admin:permissions"),
      () => api.get(API_PATHS.admin.permissions())
    )
      .then((response) => {
        if (!mounted) return;
        setPermissionRules(extractPermissionRules(response?.data));
      })
      .catch(() => {
        if (mounted) setPermissionRules([]);
      })
      .finally(() => {
        if (mounted) setPermissionReady(true);
      });

    return () => {
      mounted = false;
    };
  }, [authReady, normalizedUserRole, sessionVersion]);

  useEffect(() => {
    const handleLogout = (event) => {
      StudentService.clearCachedProfile();
      TeacherService.clearCachedProfile();
      setUserRole(null);
      setOnboardingUserIdentity("");
      if (event?.detail?.reason && event.detail.reason !== "manual") {
        setSessionExpired(true);
        setSessionModal({ mode: null, message: "" });
        return;
      }

      setSessionModal({ mode: null, message: "" });
    };

    const handleSessionWarning = (event) => {
      if (!userRoleRef.current) return;

      setSessionModal({
        mode: "warning",
        message:
          event?.detail?.message ||
          "Your session is about to expire. Refresh it now to continue without interruption.",
      });
    };

    const handleSessionRefreshed = () => {
      setSessionExpired(false);
      setSessionModal({ mode: null, message: "" });
      setSessionVersion((current) => current + 1);
    };

    window.addEventListener("auth:logout", handleLogout);
    window.addEventListener("auth:session-warning", handleSessionWarning);
    window.addEventListener("auth:session-refreshed", handleSessionRefreshed);

    return () => {
      window.removeEventListener("auth:logout", handleLogout);
      window.removeEventListener("auth:session-warning", handleSessionWarning);
      window.removeEventListener("auth:session-refreshed", handleSessionRefreshed);
    };
  }, []);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (![
        "token",
        "accessToken",
        "refreshToken",
        "user",
      ].includes(event.key)) return;

      const snapshot = getStoredAuthSnapshot();
      if (userRoleRef.current && !snapshot.hasSession) {
        terminateSession({
          reason: "expired",
          message: "Your session has expired. Please login again.",
        });
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  useEffect(() => {
    if (userRole) {
      setSessionExpired(false);
      setSessionModal({ mode: null, message: "" });
    }
  }, [userRole]);

  const closeSessionModal = () => {
    if (sessionModal.mode === "expired") return;
    setSessionModal({ mode: null, message: "" });
  };

  const handleContinueSession = async () => {
    setIsRefreshingSession(true);

    try {
      await requestSessionRefresh();
    } catch (error) {
      terminateSession({
        reason: "expired",
        message: error?.message || "Your session could not be refreshed. Please login again.",
      });
    } finally {
      setIsRefreshingSession(false);
    }
  };

  const isLoggedIn = authReady && Boolean(userRole && !sessionExpired);

  useEffect(() => {
    if (!isLoggedIn) return undefined;

    let heartbeatInFlight = false;
    const sendPresenceHeartbeat = async () => {
      if (document.visibilityState !== "visible") return;
      if (heartbeatInFlight) return;
      heartbeatInFlight = true;
      try {
        // Refresh the readable CSRF cookie before each heartbeat. The API
        // client copies its value into X-XSRF-TOKEN and includes credentials.
        await initialiseCsrf({ force: true });
        await api.post(API_PATHS.auth.presenceHeartbeat, undefined, {
          withCredentials: true,
        });
      } catch {
        // Presence is best-effort and will be retried on the next interval.
      } finally {
        heartbeatInFlight = false;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") sendPresenceHeartbeat();
    };

    sendPresenceHeartbeat();
    const intervalId = window.setInterval(sendPresenceHeartbeat, 60000);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isLoggedIn]);

  // Listen for subscription:expired events dispatched by httpClient.
  // Kept in App's own stable useEffect (not a nested component) so the
  // listener is never accidentally dropped during a re-render.
  useEffect(() => {
    const handle = () => {
      const role = userRoleRef.current;
      if (role && role !== ROLE_SUPER_ADMIN && window.location.pathname !== "/subscription-expired") {
        window.location.replace("/subscription-expired");
      }
    };
    window.addEventListener("subscription:expired", handle);
    return () => window.removeEventListener("subscription:expired", handle);
  }, []); // eslint-disable-line

  // SubscriptionGuard: if tenant subscription is blocked, redirect non-super-admins
  // to the /subscription-expired page instead of their normal dashboard.
  // Waits for subscriptionReady so we never flash-allow access before first API check.
  const SubscriptionGuard = ({ children }) => {
    const { activeTenant, subscriptionError, subscriptionLoading, subscriptionReady, refreshSubscriptionStatus } = useTenant();
    if (!subscriptionReady || subscriptionLoading) {
      return (
        <ClassNXLoader variant="page" label="Checking subscription" detail="Confirming your school access." />
      );
    }
    if (subscriptionError) {
      return (
        <div className="flex min-h-screen items-center justify-center px-6">
          <div className="w-full max-w-md rounded-3xl border border-amber-200 bg-amber-50 p-6 text-center shadow-lg">
            <h2 className="text-xl font-black text-amber-950">Subscription status unavailable</h2>
            <p className="mt-2 text-sm leading-6 text-amber-900">
              We could not verify your subscription. Your platform access is paused until the status can be confirmed.
            </p>
            <button
              type="button"
              onClick={refreshSubscriptionStatus}
              className="mt-5 rounded-full bg-amber-600 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-amber-700"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }
    if (userRole !== ROLE_SUPER_ADMIN
      && !hasSubscriptionAccess(activeTenant?.subscription)
      && ![ROLE_ADMIN, ROLE_SCHOOL].includes(userRole)) {
      return <Navigate to="/subscription-expired" replace />;
    }
    return children;
  };

  const PrivateRoute = ({
    children,
    allowedRoles = [],
    requiredModule = "",
    requiredAction = "view",
  }) => {
    const location = useLocation();
    const { activeTenant, subscriptionReady } = useTenant();
    if (!authReady) {
      return (
        <ClassNXLoader label="Checking session" detail="Confirming your access." />
      );
    }
    if (!isLoggedIn) {
      const reason = sessionExpired ? "expired" : "login_required";
      return <Navigate to="/login" replace state={{ reason }} />;
    }
    if (!allowedRoles.includes(userRole)) {
      return <Navigate to="/unauthorized" replace />;
    }
    if (userRole !== ROLE_SUPER_ADMIN && !subscriptionReady) {
      return <ClassNXLoader label="Checking subscription" detail="Confirming your school access." />;
    }
    const subscriptionBlocked = Boolean(activeTenant?.subscription)
      && !hasSubscriptionAccess(activeTenant.subscription);
    if (subscriptionBlocked
      && userRole !== ROLE_SUPER_ADMIN
      && ![ROLE_ADMIN, ROLE_SCHOOL].includes(userRole)
      && location.pathname !== "/subscription-expired") {
      return <Navigate to="/subscription-expired" replace />;
    }
    if (requiredModule && !permissionReady) {
      return (
        <ClassNXLoader label="Checking permissions" detail="Loading your workspace access." />
      );
    }
    if (
      requiredModule
      && !canAccessModule({
        role: userRole,
        module: requiredModule,
        action: requiredAction,
        rules: permissionRules,
      })
    ) {
      return (
        <Navigate
          to="/unauthorized"
          replace
          state={{
            message: `Your role does not currently have ${requiredAction} access for ${requiredModule}.`,
          }}
        />
      );
    }
    return children;
  };

  return (
    <Router>
      <StudentProfileProvider userRole={userRole}>
        <TeacherProfileProvider userRole={userRole}>
          <AppFrame
            authReady={authReady}
            isDarkMode={isDarkMode}
            isLoggedIn={isLoggedIn}
            userRole={userRole}
            setUserRole={setUserRole}
            isStudentUser={isStudentUser}
            isTeacherUser={isTeacherUser}
            isSuperAdminUser={isSuperAdminUser}
            sessionModal={sessionModal}
            isRefreshingSession={isRefreshingSession}
            closeSessionModal={closeSessionModal}
            handleContinueSession={handleContinueSession}
            permissionRules={permissionRules}
            permissionReady={permissionReady}
          >
              {isLoggedIn && <FirstLoginTour role={userRole} userIdentity={onboardingUserIdentity} />}
              <Routes>
            <Route
              path="/"
              element={<LandingRoute isLoggedIn={isLoggedIn} userRole={userRole} setUserRole={setUserRole} />}
            />
            <Route
              path="/home"
              element={<LandingRoute isLoggedIn={isLoggedIn} userRole={userRole} setUserRole={setUserRole} />}
            />
            <Route
              path="/gallery"
              element={
                <PrivateRoute allowedRoles={ADMIN_ROLES}>
                  <SubscriptionGuard><Gallery /></SubscriptionGuard>
                </PrivateRoute>
              }
            />
            <Route path="/services" element={<Services />} />
            <Route path="/services/:serviceSlug" element={<ServiceDetail />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route
              path="/profile"
              element={
                <PrivateRoute allowedRoles={ALL_APP_ROLES}>
                  <Profile />
                </PrivateRoute>
              }
            />
            <Route
              path="/login"
              element={
                isLoggedIn && userRole
                  ? <Navigate to={getDashboardPathForRole(userRole)} replace />
                  : <Login setUser={setUserRole} />
              }
            />
            <Route path="/logged-out" element={<LoggedOut />} />
            <Route path="/schools/request-demo" element={<SchoolDemoRequest />} />
            <Route path="/feedback" element={<FeedbackForm />} />
            <Route path="/guide" element={<PrivateRoute allowedRoles={ALL_APP_ROLES}><Guide role={userRole} /></PrivateRoute>} />
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route
              path="/subscription-plans"
              element={
                <PrivateRoute allowedRoles={[ROLE_ADMIN, ROLE_SCHOOL]}>
                  <SubscriptionPlans />
                </PrivateRoute>
              }
            />
            <Route
              path="/subscription-payment"
              element={
                <PrivateRoute allowedRoles={[ROLE_ADMIN, ROLE_SCHOOL]}>
                  <SubscriptionPayment />
                </PrivateRoute>
              }
            />
            <Route
              path="/subscription-expired"
              element={
                <PrivateRoute allowedRoles={ALL_APP_ROLES}>
                  <SubscriptionExpired />
                </PrivateRoute>
              }
            />

            <Route
              path="/teacherdashboard"
              element={<Navigate to="/teacher-dashboard" replace />}
            />

            <Route
              path="/admission-form"
              element={
                <PrivateRoute allowedRoles={STUDENT_ROLES}>
                  <SubscriptionGuard><AdmissionForm /></SubscriptionGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/view-admission-form"
              element={
                <PrivateRoute allowedRoles={STUDENT_ROLES}>
                  <SubscriptionGuard><ViewAdmissionForm /></SubscriptionGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/admission-details"
              element={
                <PrivateRoute allowedRoles={ALL_APP_ROLES}>
                  <SubscriptionGuard><AdmissionDetails userRole={userRole} /></SubscriptionGuard>
                </PrivateRoute>
              }
            />

            <Route
              path="/admin-board"
              element={
                <PrivateRoute allowedRoles={adminBoardRoles}>
                  <SubscriptionGuard>
                    <AdminDashBoard
                      userRole={userRole}
                      permissionRules={permissionRules}
                      permissionReady={permissionReady}
                    />
                  </SubscriptionGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin-home"
              element={
                <PrivateRoute allowedRoles={adminBoardRoles}>
                  <SubscriptionGuard><AdminHome /></SubscriptionGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/super-admin/home"
              element={
                <PrivateRoute allowedRoles={[ROLE_SUPER_ADMIN]}>
                  <SuperAdminHome />
                </PrivateRoute>
              }
            />
            <Route path="/teacher/home" element={<PrivateRoute allowedRoles={[ROLE_TEACHER]}><SubscriptionGuard><TeacherHome /></SubscriptionGuard></PrivateRoute>} />
            <Route
              path="/super-admin"
              element={
                <PrivateRoute allowedRoles={[ROLE_SUPER_ADMIN]}>
                  <SuperAdminSaaS />
                </PrivateRoute>
              }
            />
            <Route
              path="/student/usage"
              element={
                <PrivateRoute allowedRoles={STUDENT_ROLES}>
                  <SubscriptionGuard><StudentTimeSpent /></SubscriptionGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/teacher/students-activity"
              element={
                <PrivateRoute allowedRoles={[ROLE_TEACHER, ...MANAGEMENT_ROLES]}>
                  <SubscriptionGuard><TeacherStudentUsageView /></SubscriptionGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/plan-usage"
              element={
                <PrivateRoute allowedRoles={adminBoardRoles}>
                  <SubscriptionGuard><PlanUsage /></SubscriptionGuard>
                </PrivateRoute>
              }
            />
            <Route path="/student/library" element={<PrivateRoute allowedRoles={STUDENT_ROLES}><SubscriptionGuard><StudentLearningLibrary /></SubscriptionGuard></PrivateRoute>} />
            <Route
              path="/admin/students-activity"
              element={
                <PrivateRoute allowedRoles={adminBoardRoles}>
                  <SubscriptionGuard><TeacherStudentUsageView /></SubscriptionGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/super-admin/platform-metrics"
              element={
                <PrivateRoute allowedRoles={[ROLE_SUPER_ADMIN]}>
                  <SuperAdminPlatformMetrics />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/admissions"
              element={
                <PrivateRoute allowedRoles={adminBoardRoles} requiredModule="Admissions">
                  <SubscriptionGuard><AdminAdmissions /></SubscriptionGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <PrivateRoute allowedRoles={adminBoardRoles} requiredModule="Users">
                  <SubscriptionGuard><AdminUserManagement /></SubscriptionGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/students"
              element={
                <PrivateRoute allowedRoles={adminBoardRoles} requiredModule="Students">
                  <SubscriptionGuard><AdminStudentManagement /></SubscriptionGuard>
                </PrivateRoute>
              }
            />
            <Route path="/super-admin/tenants/new" element={<PrivateRoute allowedRoles={[ROLE_SUPER_ADMIN]}><SuperAdminTenantForm /></PrivateRoute>} />
            <Route path="/super-admin/tenants/:tenantId/edit" element={<PrivateRoute allowedRoles={[ROLE_SUPER_ADMIN]}><SuperAdminTenantForm /></PrivateRoute>} />
            <Route path="/super-admin/dev-activities/new" element={<PrivateRoute allowedRoles={[ROLE_SUPER_ADMIN]}><SuperAdminDevActivityPage create /></PrivateRoute>} />
            <Route path="/super-admin/dev-activities/:activityId" element={<PrivateRoute allowedRoles={[ROLE_SUPER_ADMIN]}><SuperAdminDevActivityPage /></PrivateRoute>} />
            <Route path="/super-admin/customization-requests" element={<PrivateRoute allowedRoles={[ROLE_SUPER_ADMIN]}><SuperAdminCustomizationRequests /></PrivateRoute>} />
            <Route path="/super-admin/learning-resources" element={<PrivateRoute allowedRoles={[ROLE_SUPER_ADMIN]}><SuperAdminLearningResources /></PrivateRoute>} />
            <Route path="/super-admin/branding-approvals" element={<PrivateRoute allowedRoles={[ROLE_SUPER_ADMIN]}><SuperAdminBrandingApprovals /></PrivateRoute>} />
            <Route path="/super-admin/school-leads" element={<PrivateRoute allowedRoles={[ROLE_SUPER_ADMIN]}><SuperAdminSchoolLeads /></PrivateRoute>} />
            <Route path="/super-admin/employees" element={<PrivateRoute allowedRoles={[ROLE_SUPER_ADMIN]}><SuperAdminEmployeeManagement /></PrivateRoute>} />
            <Route path="/super-admin/employees/new" element={<PrivateRoute allowedRoles={[ROLE_SUPER_ADMIN]}><SuperAdminEmployeeCreate /></PrivateRoute>} />
            <Route path="/super-admin/login-ads" element={<PrivateRoute allowedRoles={[ROLE_SUPER_ADMIN]}><SuperAdminLoginAds /></PrivateRoute>} />
            <Route
              path="/admin/students/create"
              element={
                <PrivateRoute allowedRoles={[ROLE_ADMIN]}>
                  <SubscriptionGuard><AdminStudentCreate /></SubscriptionGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/students/import"
              element={
                <PrivateRoute allowedRoles={[ROLE_ADMIN]}>
                  <SubscriptionGuard><AdminStudentImport /></SubscriptionGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/students/customization-request"
              element={<PrivateRoute allowedRoles={[ROLE_ADMIN]}><SubscriptionGuard><AdminStudentCustomizationRequest /></SubscriptionGuard></PrivateRoute>}
            />
            <Route path="/admin/customization-requests" element={<PrivateRoute allowedRoles={[ROLE_ADMIN]}><SubscriptionGuard><AdminCustomizationRequest /></SubscriptionGuard></PrivateRoute>} />
            <Route path="/admin/learning-resources" element={<PrivateRoute allowedRoles={adminBoardRoles}><SubscriptionGuard><AdminLearningResourceRequests /></SubscriptionGuard></PrivateRoute>} />
            <Route path="/settings/branding" element={<PrivateRoute allowedRoles={[ROLE_ADMIN, ROLE_SCHOOL]}><SubscriptionGuard><TenantBranding /></SubscriptionGuard></PrivateRoute>} />
            <Route path="/admin/home-slider" element={<PrivateRoute allowedRoles={[ROLE_ADMIN, ROLE_SCHOOL]}><SubscriptionGuard><SchoolHomeSliderManager /></SubscriptionGuard></PrivateRoute>} />
            <Route
              path="/admin/import/:moduleKey"
              element={
                <PrivateRoute allowedRoles={[ROLE_ADMIN]}>
                  <SubscriptionGuard><AdminModuleImport /></SubscriptionGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/students/:studentId/edit"
              element={
                <PrivateRoute allowedRoles={[ROLE_ADMIN]}>
                  <SubscriptionGuard><AdminStudentEdit /></SubscriptionGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/teachers"
              element={
                <PrivateRoute allowedRoles={adminBoardRoles} requiredModule="Teachers">
                  <SubscriptionGuard><AdminTeacherManagement /></SubscriptionGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/teachers/create"
              element={
                <PrivateRoute allowedRoles={[ROLE_ADMIN]}>
                  <SubscriptionGuard><AdminTeacherCreate /></SubscriptionGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/class-subjects"
              element={
                <PrivateRoute allowedRoles={adminBoardRoles} requiredModule="Classes & Subjects">
                  <SubscriptionGuard><AdminClassSubjectManagement /></SubscriptionGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/timetable"
              element={
                <PrivateRoute allowedRoles={adminBoardRoles} requiredModule="Timetable">
                  <SubscriptionGuard><AdminTimetableManagement /></SubscriptionGuard>
                </PrivateRoute>
              }
            />
            <Route path="/admin/class-subjects/classes/create" element={<PrivateRoute allowedRoles={adminBoardRoles} requiredModule="Classes & Subjects"><SubscriptionGuard><AdminClassSubjectManagement createMode="class" /></SubscriptionGuard></PrivateRoute>} />
            <Route path="/admin/class-subjects/subjects/create" element={<PrivateRoute allowedRoles={adminBoardRoles} requiredModule="Classes & Subjects"><SubscriptionGuard><AdminClassSubjectManagement createMode="subject" /></SubscriptionGuard></PrivateRoute>} />
            <Route path="/admin/timetable/create" element={<PrivateRoute allowedRoles={adminBoardRoles} requiredModule="Timetable"><SubscriptionGuard><AdminTimetableManagement createMode /></SubscriptionGuard></PrivateRoute>} />
            <Route
              path="/admin/exams"
              element={
                <PrivateRoute allowedRoles={adminBoardRoles} requiredModule="Exams">
                  <SubscriptionGuard><AdminExamManagement /></SubscriptionGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/fees"
              element={
                <PrivateRoute allowedRoles={adminBoardRoles} requiredModule="Fees">
                  <SubscriptionGuard><AdminFeeManagement /></SubscriptionGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/notifications"
              element={
                <PrivateRoute allowedRoles={adminBoardRoles} requiredModule="Notifications">
                  <SubscriptionGuard><AdminNotificationsManagement /></SubscriptionGuard>
                </PrivateRoute>
              }
            />
            <Route path="/admin/notifications/create" element={<PrivateRoute allowedRoles={adminBoardRoles} requiredModule="Notifications"><SubscriptionGuard><AdminNotificationsManagement createMode /></SubscriptionGuard></PrivateRoute>} />
            <Route
              path="/admin/permissions"
              element={
                <PrivateRoute allowedRoles={adminBoardRoles} requiredModule="Permissions">
                  <SubscriptionGuard><AdminPermissionMatrix /></SubscriptionGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/parent-portal"
              element={
                <PrivateRoute allowedRoles={adminBoardRoles} requiredModule="Parent Portal">
                  <SubscriptionGuard><AdminParentPortal /></SubscriptionGuard>
                </PrivateRoute>
              }
            />

            <Route
              path="/school-dashboard"
              element={
                <PrivateRoute allowedRoles={[ROLE_SCHOOL, ...STAFF_ROLES]}>
                  <SubscriptionGuard><SchoolDashboard /></SubscriptionGuard>
                </PrivateRoute>
              }
            />

            <Route
              path="/teacher-dashboard"
              element={
                <PrivateRoute allowedRoles={[ROLE_TEACHER]}>
                  <SubscriptionGuard>
                    <TeacherDashboard />
                  </SubscriptionGuard>
                </PrivateRoute>
              }
            />

            <Route
              path="/user-dashboard"
              element={
                <PrivateRoute allowedRoles={[...STUDENT_ROLES, ...PARENT_ROLES]}>
                  <SubscriptionGuard>
                    <UserDashBoard />
                  </SubscriptionGuard>
                </PrivateRoute>
              }
            />
            <Route path="/admin/parent-portal/create" element={<PrivateRoute allowedRoles={adminBoardRoles} requiredModule="Parent Portal"><SubscriptionGuard><AdminParentPortal createMode /></SubscriptionGuard></PrivateRoute>} />
            <Route path="/principal" element={<PrivateRoute allowedRoles={[ROLE_PRINCIPAL]}><SubscriptionGuard><PrincipalDashboard /></SubscriptionGuard></PrivateRoute>} />

            <Route
              path="/classroom/room/:roomCode"
              element={
                <PrivateRoute allowedRoles={[ROLE_TEACHER, ...STUDENT_ROLES]}>
                  <SubscriptionGuard><ClassroomMeetingPage userRole={userRole} /></SubscriptionGuard>
                </PrivateRoute>
              }
            />

            <Route
              path="/classroom/join/:roomCode"
              element={
                <PrivateRoute allowedRoles={STUDENT_ROLES}>
                  <SubscriptionGuard>
                    <UserDashBoard />
                  </SubscriptionGuard>
                </PrivateRoute>
              }
            />

            <Route path="/moments" element={
              <PrivateRoute allowedRoles={ALL_APP_ROLES}>
                <SubscriptionGuard><MomentsPage userRole={userRole} /></SubscriptionGuard>
              </PrivateRoute>
            } />

            <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
          </AppFrame>
        </TeacherProfileProvider>
      </StudentProfileProvider>
    </Router>
  );
};

export default App;
